import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import './styles/Upload.css';
import { User } from './types';
import { api } from './utils/api';
import {
    Autocomplete,
    TextField,
    Button,
    Container,
    Typography,
    Box,
    CircularProgress,
    Alert,
    ThemeProvider,
    createTheme,
} from '@mui/material';
import { palette } from './styles/colors';

const theme = createTheme({
    palette: {
        primary: palette.primary,
        secondary: palette.secondary,
    },
});

export default function UploadPage(): JSX.Element {
    const [email, setEmail] = useState('');
    const [project, setProject] = useState('');
    const [tags, setTags] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ uploaded: number; total: number } | null>(null);
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);
    const [message, setMessage] = useState<string | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    const inputRef = useRef<HTMLInputElement | null>(null);

    function onFileChange(e: ChangeEvent<HTMLInputElement>) {
        const list = e.target.files;
        if (!list) return;
        const arr = Array.from(list);
        setFiles(arr);
        const urls = arr.map((f) => URL.createObjectURL(f));
        setPreviews(urls);
    }

    useEffect(() => {
        try {
            const em = localStorage.getItem('upload:email');
            const p = localStorage.getItem('upload:project');
            if (em) setEmail(em);
            if (p) setProject(p);
        } catch (err) {
            console.debug('localStorage not available', err);
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        async function loadUsers() {
            try {
                const res = await api.get('/listUsers');
                if (!res.ok) {
                    console.debug('failed to load users', res.status);
                    return;
                }
                const data: User[] = await res.json() as User[];
                if (mounted) setUsers(data || []);
            } catch (err) {
                console.debug('error loading users', err);
            }
        }
        loadUsers();
        return () => { mounted = false; };
    }, []);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setMessage(null);
        setUploadErrors([]);
        setUploadProgress(null);

        if (!selectedUser || !email || !project) {
            setMessage('Please fill Client, Email and Project');
            return;
        }
        if (files.length === 0) {
            setMessage('Please select at least one image to upload');
            return;
        }

        setUploading(true);
        setUploadProgress({ uploaded: 0, total: files.length });

        const CONCURRENCY_LIMIT = 5;
        const filesToUpload = [...files]; // Create a mutable copy of the files array
        const failedUploads: string[] = [];

        try {
            const uploadWorker = async () => {
                while (filesToUpload.length > 0) {
                    const file = filesToUpload.shift();
                    if (!file) continue;

                    try {
                        // 1. Get pre-signed URL from your server
                        const presignedUrlResponse = await api.post('/api/getPresignedUploadURL', {
                            filename: file.name,
                            contentType: file.type,
                        });

                        if (!presignedUrlResponse.ok) {
                            throw new Error(`Failed to get pre-signed URL for ${file.name}`);
                        }
                        const { url, key } = await presignedUrlResponse.json();

                        // 2. Upload file directly to S3
                        const uploadResponse = await fetch(url, {
                            method: 'PUT',
                            body: file,
                        });

                        if (!uploadResponse.ok) {
                            throw new Error(`S3 upload failed for ${file.name}`);
                        }

                        // 3. Notify your server about the successful upload
                        await api.post('/api/notifyUpload', {
                            s3Key: key,
                            client: selectedUser?.ID,
                            project,
                            tags,
                        });

                        // Update progress after a successful upload
                        setUploadProgress(progress => progress ? { ...progress, uploaded: progress.uploaded + 1 } : null);

                    } catch (error) {
                        console.error(`Error uploading ${file.name}:`, error);
                        failedUploads.push(file.name);
                    }
                }
            };

            const workers = Array(CONCURRENCY_LIMIT).fill(null).map(() => uploadWorker());
            await Promise.all(workers);

            if (failedUploads.length > 0) {
                setUploadErrors(failedUploads);
                setMessage(`Upload finished with ${failedUploads.length} error(s).`);
            } else {
                setMessage(`Upload success: ${files.length} file(s) processed`);
                // Clear form on full success
                setSelectedUser(null);
                setEmail('');
                setProject('');
                setTags('');
                setFiles([]);
                setPreviews([]);
                if (inputRef.current) inputRef.current.value = '';
            }

        } catch (err: any) {
            setMessage(`An unexpected error occurred: ${err?.message || err}`);
        } finally {
            setUploading(false);
        }
    }

    useEffect(() => {
        return () => {
            previews.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [previews]);

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth="md" className="upload-page-root">
                <Box className="upload-card">
                    <Typography variant="h4" component="h2" gutterBottom>
                        Upload Images
                    </Typography>
                    <form onSubmit={onSubmit} className="upload-form">
                        <Autocomplete
                            options={users}
                            getOptionLabel={(option) => option.Username}
                            value={selectedUser}
                            onChange={(_e, newValue) => {
                                setSelectedUser(newValue);
                                if (newValue) setEmail(newValue.Email || '');
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Assign to client"
                                    placeholder="Search username or email"
                                    variant="outlined"
                                    className="upload-input"
                                />
                            )}
                        />
                        <TextField
                            label="Email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                try {
                                    localStorage.setItem('upload:email', e.target.value);
                                } catch (err) {
                                    /* ignore */
                                }
                            }}
                            placeholder="email@example.com"
                            type="email"
                            variant="outlined"
                            fullWidth
                            className="upload-input"
                        />
                        <TextField
                            label="Project"
                            value={project}
                            onChange={(e) => {
                                setProject(e.target.value);
                                try {
                                    localStorage.setItem('upload:project', e.target.value);
                                } catch (err) {
                                    /* ignore */
                                }
                            }}
                            placeholder="Project name"
                            variant="outlined"
                            fullWidth
                            className="upload-input"
                        />
                        <TextField
                            label="Tags (comma-separated)"
                            value={tags}
                            onChange={(e) => setTags(e.target.value)}
                            placeholder="e.g. nature, cityscape, portrait"
                            variant="outlined"
                            fullWidth
                            className="upload-input"
                        />
                        <Button variant="contained" component="label" color="primary">
                            Select Images
                            <input
                                ref={inputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={onFileChange}
                            />
                        </Button>

                        {previews.length > 0 && (
                            <div className="preview-grid">
                                {previews.map((p, i) => (
                                    <div key={i} className="preview-item">
                                        <img src={p} alt={`preview-${i}`} />
                                    </div>
                                ))}
                            </div>
                        )}

                        <Box className="upload-actions">
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={uploading}
                                startIcon={uploading && <CircularProgress size={20} />}
                            >
                                {uploading ? 'Uploading…' : 'Upload'}
                            </Button>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={() => {
                                    setFiles([]);
                                    setPreviews([]);
                                    try {
                                        localStorage.removeItem('upload:client');
                                        localStorage.removeItem('upload:email');
                                        localStorage.removeItem('upload:project');
                                    } catch (e) {
                                        /* ignore */
                                    }
                                    if (inputRef.current) inputRef.current.value = '';
                                }}
                            >
                                Clear
                            </Button>
                        </Box>
                    </form>
                    {uploadProgress && (
                        <Box sx={{ mt: 2 }}>
                            <Typography>
                                Progress: {uploadProgress.uploaded} / {uploadProgress.total}
                            </Typography>
                        </Box>
                    )}
                    {message && (
                        <Alert severity={uploadErrors.length > 0 ? 'error' : 'success'} className="upload-message">
                            {message}
                            {uploadErrors.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2">Failed files:</Typography>
                                    <ul>
                                        {uploadErrors.map((file, index) => (
                                            <li key={index}>{file}</li>
                                        ))}
                                    </ul>
                                </Box>
                            )}
                        </Alert>
                    )}
                </Box>
            </Container>
        </ThemeProvider>
    );
}
